import { apiClient } from "./client";
import type { Insight } from "./insightsApi";

// Release 1.6.0 (O-R81, O-R82): the Planner Cockpit and the "Do in GSS" queue. Mirrors
// SummitOperations.Contracts/PlannerCockpit/PlannerCockpitDtos.cs. Every endpoint answers 404 while the governed switch
// planning.cockpit.enabled is off; the screens then say so instead of showing an error. GSS is never written.

export type GssUpdateStatus = "open" | "done" | "confirmed" | "superseded" | "cancelled";

export interface GssUpdateItem {
  id: number;
  workOrderKey: string;
  workOrderDisplay: string;
  operationKey: string | null;
  sequence: number | null;
  field: string;
  fieldLabel: string;
  gssTable: string;
  gssKey: string;
  gssColumn: string;
  screen: string;
  oldValue: string;
  oldDisplay: string;
  newValue: string;
  newDisplay: string;
  gssNowShows: string;
  reason: string;
  decisionKind: string;
  decisionLabel: string;
  decisionId: string;
  decisionLink: string | null;
  decidedByName: string;
  decidedAtUtc: string;
  assigneeUpn: string;
  assigneeName: string;
  status: GssUpdateStatus;
  statusLabel: string;
  createdAtUtc: string;
  ageWorkdays: number;
  doneAtUtc: string | null;
  doneByName: string | null;
  importsSinceDone: number;
  stillDiffers: boolean;
  confirmedAtUtc: string | null;
  supersededAtUtc: string | null;
  supersededReason: string | null;
  canTick: boolean;
  canReassign: boolean;
  valueKind: string;
  /** Release 1.20.0 (O-R222): the one-click copy pills — the GSS reference, the exact value and the standardised note. */
  copyReference?: string;
  copyValue?: string;
  copyNote?: string;
}

export interface GssQueueCounts {
  open: number;
  done: number;
  stillDiffers: number;
  confirmedLast7Days: number;
  mine: number;
}

export interface GssImportStamp {
  importId: number | null;
  finishedAtUtc: string | null;
  valueKind: string;
}

export interface GssUpdateQueue {
  scope: "mine" | "all";
  status: string;
  canSeeAll: boolean;
  counts: GssQueueCounts;
  lastImport: GssImportStamp;
  stillDiffersAfterImports: number;
  items: GssUpdateItem[];
}

export interface GssUpdateCount {
  mine: number;
  mineStillDiffers: number;
  all: number | null;
}

export interface GssAssignee {
  upn: string;
  name: string;
}

export interface ReleaseGate {
  key: string;
  label: string;
  /** go | attention | no-go | not-tracked */
  state: string;
  words: string;
  owner: string;
}

export interface ReleaseCandidate {
  workOrderKey: string;
  display: string;
  partNumber: string;
  customerName: string | null;
  priority: string;
  needBy: string | null;
  leadWorkdays: number;
  leadFromPlan: boolean;
  latestRelease: string | null;
  windowOpens: string | null;
  /** open | past */
  windowState: string;
  workdaysPast: number;
  isGo: boolean;
  attention: number;
  firstBlocker: string | null;
  gates: ReleaseGate[];
  markedForRelease: boolean;
  releaseDecisionId: number | null;
  valueKind: string;
}

export interface BlockedGroup {
  blockerKey: string;
  label: string;
  owner: string;
  count: number;
  oldestWorkdaysPast: number;
  items: ReleaseCandidate[];
}

export interface AtRiskTopLevel {
  workOrderKey: string;
  display: string;
  partNumber: string;
  customerName: string | null;
  healthTier: string;
  needBy: string | null;
  plannedFinish: string | null;
  slackWorkdays: number;
  isLate: boolean;
  brokenLinkKey: string | null;
  brokenLinkDisplay: string | null;
  brokenLinkReason: string | null;
  valueKind: string;
}

export interface Overload {
  workcenterCode: string;
  day: string;
  capacityHours: number;
  loadHours: number;
  overHours: number;
  utilisationPercent: number;
  calendarLink: string;
  valueKind: string;
}

export interface WaitingOnMe {
  gssUpdates: number;
  gssStillDiffers: number;
  topGssUpdates: GssUpdateItem[];
  rebalanceAuthorisations: number | null;
  rebalanceLink: string | null;
}

export interface OvernightChange {
  workOrderKey: string;
  display: string;
  kind: string;
  kindLabel: string;
  oldValue: string | null;
  newValue: string | null;
  fromQueue: boolean;
  detectedAtUtc: string;
  valueKind: string;
}

export interface Overnight {
  sinceUtc: string;
  total: number;
  byKind: Record<string, number>;
  changes: OvernightChange[];
}

export interface PlannerCockpit {
  statusDate: string;
  generatedAtUtc: string;
  planHash: string;
  dataVersion: string;
  releaseWindowDays: number;
  capacityDays: number;
  readyToRelease: ReleaseCandidate[];
  blocked: BlockedGroup[];
  atRisk: AtRiskTopLevel[];
  overloads: Overload[];
  waitingOnMe: WaitingOnMe;
  overnight: Overnight;
  lastImport: GssImportStamp;
  links: { calendar: string; rebalance: string; gssUpdates: string; gantt: string };
  valueKind: string;
}

export interface CockpitDecision {
  id: number;
  workOrderKey: string;
  kind: string;
  reason: string | null;
  actorName: string;
  atUtc: string;
  batchId: string | null;
}

export interface CockpitDecisionResult {
  decisions: CockpitDecision[];
  refused: string[];
  queue: { created: number; confirmed: number; superseded: number; flagged: number };
}

/** The VIPER Rebalance page (viper-rebalance, 1.6.0). One constant so the link can move with that page. */
export const REBALANCE_PATH = "/planning/viper-rebalance";

export const PLANNER_COCKPIT_QK = {
  cockpit: ["planning", "cockpit"] as const,
  planOfAttack: ["planning", "cockpit", "plan-of-attack"] as const,
  queue: (scope: string, status: string) => ["planning", "gss-updates", scope, status] as const,
  count: ["planning", "gss-updates", "count"] as const,
  assignees: ["planning", "gss-updates", "assignees"] as const
};

export const plannerCockpitApi = {
  getCockpit: async () => (await apiClient.get<PlannerCockpit>("/planning/cockpit")).data,
  getPlanOfAttack: async () => (await apiClient.get<Insight>("/planning/cockpit/plan-of-attack")).data,
  markForRelease: async (workOrderKeys: string[], reason?: string) =>
    (await apiClient.post<CockpitDecisionResult>("/planning/cockpit/release", { workOrderKeys, reason: reason ?? null })).data,
  hold: async (workOrderKey: string, hold: boolean, reason: string) =>
    (await apiClient.post<CockpitDecisionResult>("/planning/cockpit/hold", { workOrderKey, hold, reason })).data,
  withdraw: async (decisionId: number, reason?: string) =>
    (await apiClient.post<CockpitDecisionResult>(`/planning/cockpit/decisions/${encodeURIComponent(String(decisionId))}/withdraw`, { reason: reason ?? null })).data,
  releaseListCsv: async (): Promise<Blob> => (await apiClient.get<Blob>("/planning/cockpit/release-list.csv", { responseType: "blob" })).data,

  getQueue: async (scope: "mine" | "all", status: string) =>
    (await apiClient.get<GssUpdateQueue>("/planning/gss-updates", { params: { scope, status } })).data,
  getCount: async () => (await apiClient.get<GssUpdateCount>("/planning/gss-updates/count")).data,
  getAssignees: async () => (await apiClient.get<GssAssignee[]>("/planning/gss-updates/assignees")).data,
  markDone: async (id: number, note?: string) =>
    (await apiClient.post<GssUpdateItem>(`/planning/gss-updates/${encodeURIComponent(String(id))}/done`, { note: note ?? null })).data,
  reopen: async (id: number, note?: string) =>
    (await apiClient.post<GssUpdateItem>(`/planning/gss-updates/${encodeURIComponent(String(id))}/reopen`, { note: note ?? null })).data,
  assign: async (id: number, assigneeUpn: string, assigneeName?: string) =>
    (await apiClient.post<GssUpdateItem>(`/planning/gss-updates/${encodeURIComponent(String(id))}/assign`, { assigneeUpn, assigneeName: assigneeName ?? null })).data,
  exportCsv: async (scope: "mine" | "all"): Promise<Blob> =>
    (await apiClient.get<Blob>("/planning/gss-updates/export.csv", { params: { scope }, responseType: "blob" })).data
};

/** Saves a downloaded file the way the browser does for a link. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** True when the API says the feature is not switched on (404 from every cockpit endpoint). */
export function isSwitchedOff(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && (error as { status: number }).status === 404;
}
